import React, { useState, useEffect } from 'react';
import storylineData from '../data/storylineNotes.json';

// Function to extract YouTube video ID and create proper embed URL
const getYouTubeEmbedUrl = (url) => {
  try {
    // Extract video ID using regex
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    // If match found, return proper embed URL
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    
    // Fallback: try simple replacement if regex fails
    if (url.includes('watch?v=')) {
      return url.replace('watch?v=', 'embed/');
    }
    
    // If all else fails, return original URL
    return url;
  } catch (error) {
    console.error('Error formatting YouTube URL:', error);
    return url;
  }
};

const StorylineOverlay = ({ currentTime, inPanel = false, videoControls, textSize = 100 }) => {
  // Create a style object for the text size
  const textSizeStyle = { fontSize: `${textSize}%` };
  const [currentNote, setCurrentNote] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  
  useEffect(() => {
    // Find the note that corresponds to the current time
    const findCurrentNote = () => {
      // Buffer time (in seconds) to keep a note visible after its timestamp
      const bufferTime = 5;
      
      // Find the note that matches the current time
      const note = storylineData.find(note => {
        return currentTime >= note.seconds && currentTime < note.seconds + bufferTime;
      });
      
      setCurrentNote(note || null);
    };
    
    findCurrentNote();
  }, [currentTime]);
  
  if (!currentNote) {
    return null;
  }
  
  // Function to render the note content
  const renderNoteContent = () => {
    if (inPanel) {
      return (
        <div className="panel-note">
          <div className="flex items-start">
            <div className="mr-2 text-yellow-400 font-bold">
              {currentNote.timestamp}
            </div>
            <div className="flex-1">
              <div>
                <p className="text-white mb-2" style={textSizeStyle}>{currentNote.note}</p>
                
                {currentNote.hasLink && (
                  <div className="mt-2">
                    <button 
                      onClick={() => {
                        if (videoControls) videoControls.pause();
                        setModalContent({
                          title: currentNote.linkText,
                          url: currentNote.linkUrl
                        });
                        setShowModal(true);
                      }}
                      className="inline-block bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm whitespace-nowrap"
                      style={textSizeStyle}
                    >
                      {currentNote.linkText}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Default overlay style for video
      return (
        <div className="video-overlay animate-fade-in">
          <div className="flex items-start">
            <div className="mr-2 text-yellow-400 font-bold">
              {currentNote.timestamp}
            </div>
            <div className="flex-1">
              <div className="flex items-center">
                <p className="text-white text-lg mr-4 flex-shrink" style={textSizeStyle}>{currentNote.note}</p>
                
                {currentNote.hasLink && (
                  <button 
                    onClick={() => {
                      if (videoControls) videoControls.pause();
                      setModalContent({
                        title: currentNote.linkText,
                        url: currentNote.linkUrl
                      });
                      setShowModal(true);
                    }}
                    className="inline-block bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm whitespace-nowrap flex-shrink-0"
                    style={textSizeStyle}
                  >
                    {currentNote.linkText}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
  };
  
  return (
    <>
      {/* Render the storyline note */}
      {renderNoteContent()}
      
      {/* Modal for displaying embedded content */}
      {showModal && modalContent && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold text-white">{modalContent.title}</h3>
              <button 
                onClick={() => {
                  setShowModal(false);
                  setModalContent(null);
                  if (videoControls) videoControls.play();
                }}
                className="text-gray-400 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4">
              {modalContent.url.includes('youtube.com') ? (
                <div className="aspect-video">
                  <iframe 
                    src={getYouTubeEmbedUrl(modalContent.url)}
                    title={modalContent.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <iframe 
                  src={modalContent.url}
                  title={modalContent.title}
                  className="w-full h-[60vh]"
                  frameBorder="0"
                ></iframe>
              )}
            </div>
            
            <div className="flex justify-between">
              {/* Show Reference button */}
              <button
                onClick={() => {
                  window.open(modalContent.url, '_blank');
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
              >
                Show Reference
              </button>
              
              <button
                onClick={() => {
                  setShowModal(false);
                  setModalContent(null);
                  if (videoControls) videoControls.play();
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StorylineOverlay;
